import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC
} from 'react';
import { createPortal } from 'react-dom';
import { useAui } from '@assistant-ui/react';
import {
  ChevronLeftIcon,
  HammerIcon,
  PaperclipIcon,
  PlusIcon,
  SearchIcon,
  XIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  MCP_SLASH_MODE_SKILLS,
  type McpSlashSkill
} from '../config/mcpSlashSkills';
import { useListMcpChatSkillsQuery } from '../api/mcpApi';
import type { McpChatSkill } from '../types/mcpApiTypes';
import { buildSkillUserMessage } from '../utils/skillMarkers';
import { useOptionalMcpHelpMode } from './McpHelpModeContext';

const ROOT_MENU_WIDTH = 170;
const SKILLS_MENU_WIDTH = 220;
const MENU_GAP = 6;

function toSlashSkill(skill: McpChatSkill): McpSlashSkill {
  return {
    id: skill.id,
    command: skill.command,
    title: skill.title,
    description: skill.shortDescription || skill.description,
    category: skill.category,
    prompt: skill.composerHint ?? '',
    source: 'api'
  };
}

function filterSkills(skills: McpSlashSkill[], query: string): McpSlashSkill[] {
  const q = query.trim().toLowerCase();
  if (!q) return skills;
  return skills.filter(skill => {
    const hay = [skill.command, skill.title, skill.description, skill.category]
      .join(' ')
      .toLowerCase();
    return hay.includes(q) || skill.command.startsWith(q);
  });
}

function appendThreadMessage(aui: ReturnType<typeof useAui>, text: string) {
  try {
    const thread =
      typeof (aui as { thread?: unknown }).thread === 'function'
        ? (
            aui as {
              thread: () => { append: (message: string) => void };
            }
          ).thread()
        : (
            aui as {
              thread: { append: (message: string) => void };
            }
          ).thread;
    thread.append(text);
  } catch (error) {
    console.error('Failed to append thread message for skill', error);
  }
}

function setComposerText(aui: ReturnType<typeof useAui>, text: string): void {
  try {
    const composer =
      typeof (aui as { composer?: unknown }).composer === 'function'
        ? (
            aui as {
              composer: () => { setText: (value: string) => void };
            }
          ).composer()
        : (
            aui as {
              composer: { setText: (value: string) => void };
            }
          ).composer;
    composer.setText(text);
    window.requestAnimationFrame(() => {
      const input = document.querySelector<HTMLTextAreaElement>(
        '.aui-composer-input, textarea[aria-label="Message input"]'
      );
      input?.focus();
      const cursor = text.length;
      input?.setSelectionRange(cursor, cursor);
    });
  } catch (error) {
    console.error('Failed to set composer text from plus menu skill', error);
  }
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Cursor-style composer "+" menu with left-opening cascading submenu.
 */
export const McpComposerPlusMenu: FC = () => {
  const aui = useAui();
  const helpMode = useOptionalMcpHelpMode();
  const { data: skillsData, isLoading: skillsLoading } =
    useListMcpChatSkillsQuery();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const rootMenuRef = useRef<HTMLDivElement>(null);
  const skillsMenuRef = useRef<HTMLDivElement>(null);
  const skillsItemRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);
  const [skillsOpen, setSkillsOpen] = useState(false);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const [skillsAnchor, setSkillsAnchor] = useState<DOMRect | null>(null);
  const [search, setSearch] = useState('');

  const skills = useMemo(() => {
    const apiSkills = (skillsData?.items ?? []).map(toSlashSkill);
    return [...MCP_SLASH_MODE_SKILLS, ...apiSkills];
  }, [skillsData?.items]);

  const filteredSkills = useMemo(
    () => filterSkills(skills, search),
    [skills, search]
  );

  const close = useCallback(() => {
    setOpen(false);
    setSkillsOpen(false);
    setAnchor(null);
    setSkillsAnchor(null);
    setSearch('');
  }, []);

  const syncSkillsAnchor = useCallback(() => {
    const rect = skillsItemRef.current?.getBoundingClientRect();
    if (!rect) {
      setSkillsAnchor(null);
      return;
    }
    setSkillsAnchor(rect);
  }, []);

  const openMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setAnchor(rect);
    setSkillsOpen(false);
    setSkillsAnchor(null);
    setSearch('');
    setOpen(true);
  };

  const toggle = () => {
    if (open) close();
    else openMenu();
  };

  const openSkills = () => {
    setSkillsOpen(true);
    window.requestAnimationFrame(syncSkillsAnchor);
  };

  useLayoutEffect(() => {
    if (!open || !skillsOpen) return;
    syncSkillsAnchor();
  }, [open, skillsOpen, syncSkillsAnchor]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootMenuRef.current?.contains(target)) return;
      if (skillsMenuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (skillsOpen) {
          setSkillsOpen(false);
          setSkillsAnchor(null);
          setSearch('');
          return;
        }
        close();
      }
    };
    const onReposition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) {
        close();
        return;
      }
      setAnchor(rect);
      if (skillsOpen) syncSkillsAnchor();
    };
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('keydown', onKeyDown);
      window.addEventListener('resize', onReposition);
      window.addEventListener('scroll', onReposition, true);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [close, open, skillsOpen, syncSkillsAnchor]);

  const selectSkill = useCallback(
    (skill: McpSlashSkill) => {
      if (skill.action === 'enter-help') {
        helpMode?.enterHelpMode();
        close();
        return;
      }
      if (skill.source === 'api') {
        appendThreadMessage(
          aui,
          buildSkillUserMessage({
            command: skill.command,
            title: skill.title
          })
        );
        close();
        return;
      }
      if (skill.prompt && skill.prompt.trim().length > 0) {
        setComposerText(aui, skill.prompt);
      }
      close();
    },
    [aui, close, helpMode]
  );

  const viewportPad = 12;

  /** Root menu opens upward and extends to the left of the trigger. */
  const rootMenuStyle: CSSProperties | undefined = anchor
    ? (() => {
        const gap = 8;
        const spaceAbove = Math.max(120, anchor.top - viewportPad);
        const maxHeight = Math.min(spaceAbove - gap, window.innerHeight * 0.55);
        const preferredLeft = anchor.right - ROOT_MENU_WIDTH;
        return {
          position: 'fixed',
          bottom: window.innerHeight - anchor.top + gap,
          left: clamp(
            preferredLeft,
            viewportPad,
            window.innerWidth - ROOT_MENU_WIDTH - viewportPad
          ),
          zIndex: 1080,
          maxHeight,
          width: ROOT_MENU_WIDTH
        };
      })()
    : undefined;

  /** Skills flyout cascades to the left of the root item. */
  const skillsMenuStyle: CSSProperties | undefined = skillsAnchor
    ? (() => {
        const maxHeight = Math.min(
          window.innerHeight - viewportPad * 2,
          window.innerHeight * 0.55
        );
        const preferredLeft = skillsAnchor.left - SKILLS_MENU_WIDTH - MENU_GAP;
        const left = clamp(
          preferredLeft,
          viewportPad,
          window.innerWidth - SKILLS_MENU_WIDTH - viewportPad
        );
        const preferredTop = skillsAnchor.top;
        const top = clamp(
          preferredTop,
          viewportPad,
          window.innerHeight - maxHeight - viewportPad
        );
        return {
          position: 'fixed',
          top,
          left,
          zIndex: 1081,
          maxHeight,
          width: SKILLS_MENU_WIDTH
        };
      })()
    : undefined;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          'aui-composer-add-attachment text-muted-foreground hover:text-foreground hover:bg-muted-foreground/15 dark:border-muted-foreground/15 dark:hover:bg-muted-foreground/30 size-8 self-center rounded-full active:scale-[0.96] motion-reduce:transition-none inline-flex items-center justify-center border-0 bg-transparent',
          open && 'bg-muted-foreground/15 text-foreground'
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={open ? 'بستن منو' : 'باز کردن منو'}
        onClick={toggle}
      >
        {open ? (
          <XIcon className="size-4" strokeWidth={2} />
        ) : (
          <PlusIcon className="size-4" strokeWidth={2} />
        )}
      </button>

      {open && anchor
        ? createPortal(
            <>
              <div
                ref={rootMenuRef}
                className="mcp-picker-menu d-flex flex-column border rounded-3 shadow overflow-hidden bg-white dark__bg-card-dark text-body"
                style={rootMenuStyle}
                dir="rtl"
                role="menu"
                aria-label="منوی افزودن"
              >
                <div
                  className="flex-grow-1 p-1 overflow-auto"
                  style={{ minHeight: 0 }}
                >
                  <button
                    type="button"
                    role="menuitem"
                    disabled
                    className="mcp-picker-item d-flex w-100 align-items-center justify-content-between gap-2 border-0 rounded-2 text-start px-2 py-2 bg-transparent opacity-50"
                    aria-disabled="true"
                  >
                    <span className="d-inline-flex align-items-center gap-2 min-w-0">
                      <PaperclipIcon
                        size={16}
                        strokeWidth={2}
                        className="flex-shrink-0"
                      />
                      <span className="fs--1 fw-medium">افزودن فایل</span>
                    </span>
                    <ChevronLeftIcon
                      size={14}
                      strokeWidth={2}
                      className="flex-shrink-0 opacity-45"
                    />
                  </button>

                  <button
                    ref={skillsItemRef}
                    type="button"
                    role="menuitem"
                    aria-haspopup="menu"
                    aria-expanded={skillsOpen}
                    className={cn(
                      'mcp-picker-item d-flex w-100 align-items-center justify-content-between gap-2 border-0 rounded-2 text-start px-2 py-2 bg-transparent',
                      skillsOpen && 'is-selected'
                    )}
                    onMouseEnter={openSkills}
                    onFocus={openSkills}
                    onClick={openSkills}
                  >
                    <span className="d-inline-flex align-items-center gap-2 min-w-0">
                      <HammerIcon
                        size={16}
                        strokeWidth={2}
                        className="flex-shrink-0"
                      />
                      <span className="fs--1 fw-medium">مهارت‌ها</span>
                    </span>
                    <ChevronLeftIcon
                      size={14}
                      strokeWidth={2}
                      className="flex-shrink-0 opacity-45"
                    />
                  </button>
                </div>
              </div>

              {skillsOpen && skillsAnchor ? (
                <div
                  ref={skillsMenuRef}
                  className="mcp-picker-menu d-flex flex-column border rounded-3 shadow overflow-hidden bg-white dark__bg-card-dark text-body"
                  style={skillsMenuStyle}
                  dir="rtl"
                  role="menu"
                  aria-label="مهارت‌ها"
                >
                  <div className="d-flex align-items-center gap-2 border-bottom px-2 py-2">
                    <SearchIcon
                      size={14}
                      strokeWidth={2}
                      className="flex-shrink-0 text-600"
                    />
                    <input
                      type="search"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="جستجوی مهارت‌ها"
                      className="form-control form-control-sm border-0 shadow-none bg-transparent px-0"
                      aria-label="جستجوی مهارت‌ها"
                      autoFocus
                    />
                  </div>
                  <div
                    className="flex-grow-1 p-1 overflow-auto"
                    style={{ minHeight: 0 }}
                  >
                    {skillsLoading ? (
                      <div className="px-2 py-2 fs--2 text-600">
                        در حال بارگذاری…
                      </div>
                    ) : filteredSkills.length === 0 ? (
                      <div className="px-2 py-2 fs--2 text-600">
                        مهارتی نیست
                      </div>
                    ) : (
                      filteredSkills.map(skill => (
                        <button
                          key={skill.id}
                          type="button"
                          role="menuitem"
                          className="mcp-picker-item d-flex w-100 align-items-start justify-content-between gap-2 border-0 rounded-2 text-start px-2 py-2 bg-transparent"
                          onClick={() => selectSkill(skill)}
                        >
                          <span className="d-flex flex-column min-w-0 text-start">
                            <span className="fs--1 fw-medium text-truncate">
                              {skill.title}
                            </span>
                            {skill.description ? (
                              <span className="fs--2 text-600 text-truncate">
                                {skill.description}
                              </span>
                            ) : null}
                          </span>
                          <span className="fs--2 text-600 flex-shrink-0 pt-1">
                            /{skill.command}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : null}
            </>,
            document.body
          )
        : null}
    </>
  );
};
